import React from "react"
import { cn } from "../../lib/utils"
import { AgentStatus } from "../../stores/call-store"

interface AgentAvatarProps {
  initials: string
  status: AgentStatus
  size?: "sm" | "md" | "lg"
  className?: string
}

const statusConfig: Record<
  AgentStatus,
  { ringColor: string; animate: boolean; dotColor: string }
> = {
  available: {
    ringColor: "from-success via-success/50 to-success",
    animate: true,
    dotColor: "bg-success",
  },
  away: {
    ringColor: "from-warning via-warning/50 to-warning",
    animate: false,
    dotColor: "bg-warning",
  },
  dnd: {
    ringColor: "from-danger via-danger/50 to-danger",
    animate: false,
    dotColor: "bg-danger",
  },
  on_call: {
    ringColor: "from-primary via-primary/50 to-primary",
    animate: true,
    dotColor: "bg-primary",
  },
  ringing: {
    ringColor: "from-purple-500 via-purple-300 to-purple-500",
    animate: true,
    dotColor: "bg-purple-500",
  },
}

const sizeConfig = {
  sm: {
    container: "h-10 w-10",
    ring: "h-12 w-12",
    avatar: "h-9 w-9 text-xs",
    dot: "h-2.5 w-2.5",
  },
  md: {
    container: "h-16 w-16",
    ring: "h-[72px] w-[72px]",
    avatar: "h-14 w-14 text-lg",
    dot: "h-3.5 w-3.5",
  },
  lg: {
    container: "h-20 w-20",
    ring: "h-[88px] w-[88px]",
    avatar: "h-[72px] w-[72px] text-xl",
    dot: "h-4 w-4",
  },
}

export function AgentAvatar({
  initials,
  status,
  size = "md",
  className,
}: AgentAvatarProps) {
  const config = statusConfig[status]
  const sizes = sizeConfig[size]

  return (
    <div className={cn("relative flex items-center justify-center", className)}>
      {/* Animated gradient ring */}
      <div
        className={cn(
          "absolute rounded-full bg-gradient-conic p-[2px]",
          sizes.ring,
          config.animate && "animate-status-ring"
        )}
        style={{
          background: `conic-gradient(from 0deg, var(--tw-gradient-stops))`,
        }}
      >
        <div
          className={cn(
            "absolute inset-0 rounded-full bg-gradient-to-r opacity-60",
            config.ringColor,
            config.animate && status === "ringing" && "animate-incoming-pulse"
          )}
        />
        {/* Glow effect for active states */}
        {config.animate && (
          <div
            className={cn(
              "absolute inset-0 rounded-full blur-md opacity-40 bg-gradient-to-r",
              config.ringColor
            )}
          />
        )}
      </div>

      {/* Avatar circle */}
      <div
        className={cn(
          "relative flex items-center justify-center rounded-full font-bold shadow-lg",
          "bg-[#e07a5f] text-white",
          "ring-2 ring-white/20",
          sizes.avatar,
          status === "on_call" && "animate-breathing-glow"
        )}
      >
        {initials}
      </div>

      {/* Status dot */}
      <div
        className={cn(
          "absolute bottom-0 right-0 rounded-full border-2 border-sidebar-dark shadow",
          sizes.dot,
          config.dotColor
        )}
      />
    </div>
  )
}
